/* eslint-disable react/prop-types */
/**
 * Pass I2 — engagement fallback usage card.
 *
 * Renders the ``engagement_fallback_summary`` block from the
 * campaign detail payload as three badge rows + a one-line UK-
 * spelt explainer. Uses the existing ``AirframeAeroBadge`` token
 * for the teal "Clean run" pill so the calibration card and this
 * card share the same colour vocabulary. The two fallback rows use
 * the steel-gray pill style to flag operator-opted-in fallback
 * paths.
 *
 * ``summary === null`` is the in-progress / pre-Pass-H empty
 * state — same null-handling pattern as ``EngagementCalibrationCard``.
 */
import AirframeAeroBadge from "@/components/sim/AirframeAeroBadge";
import { DEFENCE_PALETTE } from "@/components/phase19/placementUtils";


function FallbackPill({ label, testId }) {
    return (
        <span
            className={
                "inline-flex items-center rounded-full px-2 py-0.5 "
                + "text-[10px] font-medium uppercase tracking-wide "
                + "leading-none"
            }
            style={{
                background: DEFENCE_PALETTE.steelGray,
                color: "#FFFFFF",
                fontVariantNumeric: "tabular-nums",
            }}
            data-testid={testId}
        >
            {label}
        </span>
    );
}


export default function EngagementFallbackCard({ summary }) {
    if (summary == null) {
        return (
            <section
                className="rounded-md border p-4 text-sm"
                style={{
                    borderColor: DEFENCE_PALETTE.steelGray,
                    color: DEFENCE_PALETTE.steelGray,
                }}
                data-testid="engagement-fallback-card-empty"
            >
                <h3
                    className="text-[11px] font-semibold uppercase"
                    style={{ color: DEFENCE_PALETTE.navy }}
                >
                    Fallback usage
                </h3>
                <p
                    className="mt-2 leading-relaxed"
                    style={{ color: DEFENCE_PALETTE.steelGray }}
                >
                    Fallback usage not yet available — campaign in
                    progress.
                </p>
            </section>
        );
    }

    const {
        samples_total: total,
        samples_clean: nClean,
        samples_analytic_pk_fallback: nAnalytic,
        samples_kinematic_fallback: nKinematic,
    } = summary;

    return (
        <section
            className="rounded-md border p-4 text-sm"
            style={{ borderColor: DEFENCE_PALETTE.steelGray }}
            data-testid="engagement-fallback-card"
        >
            <h3
                className="text-[11px] font-semibold uppercase"
                style={{ color: DEFENCE_PALETTE.navy }}
            >
                Fallback usage
            </h3>
            <p
                className="mt-2 leading-relaxed"
                style={{
                    color: DEFENCE_PALETTE.navy,
                    fontVariantNumeric: "tabular-nums",
                }}
                data-testid="engagement-fallback-card-headline"
            >
                <span className="text-base font-semibold">
                    {total.toLocaleString()}
                </span>{" "}
                samples total — split by physics dispatch path:
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                <div
                    className="flex items-center gap-2"
                    data-testid="engagement-fallback-card-clean-row"
                >
                    <AirframeAeroBadge
                        aeroCalibrated={true}
                        title="Clean run — JSBSim 6-DoF physics used for every engagement."
                    />
                    <span
                        className="text-xs font-semibold"
                        style={{
                            color: DEFENCE_PALETTE.navy,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        Clean run · {nClean.toLocaleString()}
                    </span>
                </div>
                <div
                    className="flex items-center gap-2"
                    data-testid="engagement-fallback-card-analytic-row"
                >
                    <FallbackPill
                        label="Analytic Pk fallback"
                        testId="engagement-fallback-card-analytic-pill"
                    />
                    <span
                        className="text-xs font-semibold"
                        style={{
                            color: DEFENCE_PALETTE.navy,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {nAnalytic.toLocaleString()}
                    </span>
                </div>
                <div
                    className="flex items-center gap-2"
                    data-testid="engagement-fallback-card-kinematic-row"
                >
                    <FallbackPill
                        label="Kinematic fallback"
                        testId="engagement-fallback-card-kinematic-pill"
                    />
                    <span
                        className="text-xs font-semibold"
                        style={{
                            color: DEFENCE_PALETTE.navy,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {nKinematic.toLocaleString()}
                    </span>
                </div>
            </div>
            <p
                className="mt-3 text-xs leading-relaxed"
                style={{ color: DEFENCE_PALETTE.steelGray }}
            >
                Clean runs used JSBSim 6-DoF physics for every
                engagement. Fallback samples explicitly opted in to
                an analytic Pk roll or kinematic stepper when JSBSim
                was unavailable; the reason is journalled in{" "}
                <code
                    style={{
                        background: "#F4F6F8",
                        padding: "0 4px",
                        borderRadius: 3,
                    }}
                >
                    scenario_snapshot
                </code>.
            </p>
        </section>
    );
}
